<template>
  <div>
    <ListView
      title="Doctors"
      :enable-create="true"
      :data="data"
      :page.sync="page"
      :header="headers"
      :search.sync="search"
      @reload="reload"
      @create="openDoctorDialog()"
      @navDetails="() => {}"
    >
      <template v-slot:item.name="{ item }">
        {{ getDoctorName(item) }}
      </template>

      <template v-slot:item.enabled="{ item }">
        <v-chip :color="item.enabled ? 'success' : 'error'" small dark>
          {{ item.enabled ? 'Active' : 'Inactive' }}
        </v-chip>
      </template>

      <template v-slot:item.isAvailableForBooking="{ item }">
        <v-icon :color="item.isAvailableForBooking ? 'success' : 'grey'">
          {{ item.isAvailableForBooking ? 'mdi-check-circle' : 'mdi-close-circle' }}
        </v-icon>
      </template>

      <template v-slot:item.description="{ item }">
        {{ getDescription(item.description) }}
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click.stop="openDoctorDialog(item)" :disabled="deleting">
          mdi-pencil
        </v-icon>
        <v-icon small color="error" @click.stop="deleteDoctor(item)" :disabled="deleting">
          mdi-delete
        </v-icon>
      </template>
    </ListView>

    <!-- Doctor Edit/Create Dialog -->
    <v-dialog v-model="doctorDialog" max-width="900px" persistent>
      <DoctorCard
        v-if="doctorDialog"
        :doctor-id="selectedDoctor && selectedDoctor._id"
        :doctor-data="selectedDoctor"
        :clinic-id="clinicId"
        @close="closeDoctorDialog"
        @saved="onDoctorSaved"
      />
    </v-dialog>
  </div>
</template>

<script>
import listViewPageMixin from '@/services/listViewPageMixin';
import { mapActions } from 'vuex';
import moment from 'moment';
import DoctorCard from './DoctorCard';
import api from '@/api';

export default {
  name: 'ClinicDoctors',
  mixins: [listViewPageMixin],
  components: {
    DoctorCard,
  },
  props: {
    clinicId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      doctorDialog: false,
      selectedDoctor: null,
      deleting: false,
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Status', value: 'enabled' },
        { text: 'Show in App', value: 'isAvailableForBooking' },
        { text: 'Description', value: 'description' },
        { text: 'Created', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      populate: null,
      sort: { created_at: -1 },
    };
  },
  computed: {
    restfulURL() {
      return 'rml/admin/DoctorProfiles';
    },
    filter() {
      return this.clinicId ? { clinic: this.clinicId } : {};
    },
    title() {
      return 'Doctors';
    },
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    getDoctorName(doctor) {
      let name = '';

      // Parse firstName
      if (doctor.firstName) {
        try {
          const firstName =
            typeof doctor.firstName === 'string' ? JSON.parse(doctor.firstName) : doctor.firstName;
          name = firstName.en || '';
        } catch (e) {
          name = doctor.firstName;
        }
      }

      // Parse lastName
      if (doctor.lastName) {
        try {
          const lastName =
            typeof doctor.lastName === 'string' ? JSON.parse(doctor.lastName) : doctor.lastName;
          name = `${lastName.en || ''} ${name}`.trim();
        } catch (e) {
          name = `${doctor.lastName} ${name}`.trim();
        }
      }

      return name || 'Unnamed Doctor';
    },

    getDescription(description) {
      if (!description) return '-';
      try {
        const parsed = typeof description === 'string' ? JSON.parse(description) : description;
        return parsed.en || parsed['zh-hk'] || '-';
      } catch (e) {
        return description || '-';
      }
    },

    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : '-';
    },

    openDoctorDialog(doctor = null) {
      this.selectedDoctor = doctor;
      this.doctorDialog = true;
    },

    closeDoctorDialog() {
      this.doctorDialog = false;
      this.selectedDoctor = null;
    },

    async onDoctorSaved(doctor) {
      await this.reload();
      this.closeDoctorDialog();
    },

    async deleteDoctor(doctor) {
      const doctorName = `Dr. ${this.getDoctorName(doctor)}`;

      this.$root.$confirm
        .open('Confirm Delete', `Are you sure you want to delete ${doctorName}?`, { color: 'red' })
        .then(async confirm => {
          if (confirm) {
            this.deleting = true;
            try {
              await api.delete(`rml/admin/DoctorProfiles/${doctor._id}`);
              await this.updateAlertMessage({
                msg: 'Doctor deleted successfully',
                type: 'success',
                color: 'success',
              });
              await this.reload();
            } catch (error) {
              console.error('Error deleting doctor:', error);
              await this.updateAlertMessage({
                msg: 'Failed to delete doctor',
                type: 'error',
              });
            } finally {
              this.deleting = false;
            }
          }
        });
    },
  },
};
</script>

<style scoped></style>
